import { useState } from 'react';
import { Badge, Button, Dialog, Field, Input, Select } from '@/components/ui';
import {
  CONSENT_REJECT_REASONS,
  useAppConsentActions,
  type AppConsentForm,
  type ConsentRejectReason,
} from '@/features/encounters/api';
import { klTime } from '@/pages/calendar/time';
import styles from './consult.module.css';

const STATUS: Record<
  string,
  { label: string; tone: 'success' | 'info' | 'neutral' }
> = {
  signed: { label: 'Signed in the app', tone: 'info' },
  witnessed: { label: 'Witnessed', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'neutral' },
  revoked: { label: 'Withdrawn by the patient', tone: 'neutral' },
};

/**
 * RL-1854: consent forms the patient signed in the app for this visit. The
 * visit's doctor witnesses one after going through it with the patient, or
 * rejects it with a reason the patient sees (they can sign again).
 */
export function AppConsentForms({
  encounterId,
  forms,
  editable,
  onError,
}: {
  encounterId: string;
  forms: AppConsentForm[];
  editable: boolean;
  onError: (err: unknown) => void;
}) {
  const a = useAppConsentActions(encounterId);
  const [rejecting, setRejecting] = useState<AppConsentForm | null>(null);
  if (forms.length === 0) return null;
  return (
    <div aria-label="Consent forms signed in the app" role="group">
      <p className="eyebrow">Consent forms signed in the app</p>
      {forms.map((f) => {
        const s = STATUS[f.status] ?? { label: f.status, tone: 'neutral' };
        return (
          <p key={f.id} className={styles.procLine}>
            <span>
              <strong>{f.title}</strong> · {f.number} · signed by {f.signerName}{' '}
              {klTime(f.signedAt)}
            </span>
            <Badge tone={s.tone}>{s.label}</Badge>
            {!f.nameMatchesRecord && (
              <Badge tone="warning">Name differs from the record</Badge>
            )}
            {editable && f.status === 'signed' && (
              <Button
                size="sm"
                loading={a.witness.isPending && a.witness.variables === f.id}
                onClick={() => a.witness.mutateAsync(f.id).catch(onError)}
              >
                Witness
              </Button>
            )}
            {editable &&
              (f.status === 'signed' || f.status === 'witnessed') && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setRejecting(f)}
                >
                  Reject
                </Button>
              )}
          </p>
        );
      })}
      {rejecting && (
        <RejectDialog
          form={rejecting}
          pending={a.reject.isPending}
          onClose={() => setRejecting(null)}
          onReject={(reason, note) =>
            void a.reject
              .mutateAsync({ id: rejecting.id, reason, note })
              .then(() => setRejecting(null))
              .catch(onError)
          }
        />
      )}
    </div>
  );
}

function RejectDialog({
  form,
  pending,
  onClose,
  onReject,
}: {
  form: AppConsentForm;
  pending: boolean;
  onClose: () => void;
  onReject: (reason: ConsentRejectReason, note: string | null) => void;
}) {
  const [reason, setReason] = useState<ConsentRejectReason | ''>('');
  const [note, setNote] = useState('');
  return (
    <Dialog
      open
      variant="modal"
      title={`Reject ${form.title}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={!reason}
            loading={pending}
            onClick={() => reason && onReject(reason, note.trim() || null)}
          >
            Reject
          </Button>
        </>
      }
    >
      <p>
        The procedure needs consent again; the patient sees the reason and can
        sign again in the app.
      </p>
      <Field label="Reason">
        <Select
          value={reason}
          onChange={(e) => setReason(e.target.value as ConsentRejectReason)}
          options={[
            { value: '', label: 'Choose…' },
            ...Object.entries(CONSENT_REJECT_REASONS).map(([value, label]) => ({
              value,
              label,
            })),
          ]}
        />
      </Field>
      <Field
        label="Note for the clinic · optional"
        hint="Never shown to the patient."
      >
        <Input
          value={note}
          maxLength={300}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </Dialog>
  );
}
